import apiClient from './apiClient';
import { Company, KYBData, Document, VerificationData } from '../types';
import { getVerificationId } from '../utils/auth';

/**
 * Service for KYB-related API calls
 */
export const kybService = {
  /**
   * Fetch companies for the current user
   */
  getCompanies: async (): Promise<Company[]> => {
    return apiClient.get<Company[]>('/companies');
  },

  /**
   * Fetch a single company by ID
   */
  getCompany: async (id: string): Promise<Company> => {
    return apiClient.get<Company>(`/companies/${id}`);
  },

  /**
   * Create a new company
   */
  createCompany: async (company: Omit<Company, 'id'>): Promise<Company> => {
    return apiClient.post<Company>('/companies', company);
  },

  /**
   * Update an existing company
   */
  updateCompany: async (id: string, company: Partial<Company>): Promise<Company> => {
    return apiClient.put<Company>(`/companies/${id}`, company);
  },

  /**
   * Fetch KYB data for a company
   */
  getKYBData: async (companyId: string): Promise<KYBData> => {
    return apiClient.get<KYBData>(`/companies/${companyId}/kyb`);
  },
  
  /**
   * Fetch verification data based on JWT token
   * This is the main endpoint for getting all verification screen data
   */
  getVerification: async (): Promise<VerificationData> => {
    // If no token is present, this will fail with 401 Unauthorized
    return apiClient.get<VerificationData>('/kyb');
  },
  
  /**
   * Fetch verification data by ID (alternative to JWT approach if needed)
   */
  getVerificationById: async (verificationId: string): Promise<VerificationData> => {
    return apiClient.get<VerificationData>(`/kyb/${verificationId}`);
  },
  
  /**
   * Get current verification based on JWT token
   * Simply passes the token in the Authorization header
   */
  getCurrentVerification: async (): Promise<VerificationData> => {
    // Just call the base verification endpoint - the token will be included in the Authorization header
    return kybService.getVerification();
  },

  /**
   * Upload a document for KYB verification
   */
  uploadDocument: async (
    companyId: string,
    documentType: Document['type'],
    file: File
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', documentType);

    // Create custom headers for multipart/form-data
    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    return apiClient.post<Document>(
      `/companies/${companyId}/documents`, 
      formData,
      headers
    );
  },

  /**
   * Submit company for KYB verification
   */
  submitForVerification: async (companyId: string): Promise<KYBData> => {
    return apiClient.post<KYBData>(`/companies/${companyId}/submit`, {});
  },
};